import { useEffect, type RefObject } from 'react'

/** Loads the next page when a sentinel at the end of the list comes into view.
 *  Observed rather than measured, so the list never has to know which element
 *  is scrolling it. */
export function useInfiniteScroll(
  sentinel: RefObject<HTMLElement | null>,
  {
    hasMore,
    loading,
    onLoadMore,
  }: { hasMore: boolean; loading: boolean; onLoadMore: () => void },
): void {
  useEffect(() => {
    const node = sentinel.current
    // jsdom has no IntersectionObserver. Degrading to "no auto-load" keeps the
    // list working — the rows are all still rendered and reachable.
    if (!node || !hasMore || loading || typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onLoadMore()
      },
      // Start fetching a little before the sentinel is actually visible, so the
      // next rows are usually there by the time the list reaches them.
      { rootMargin: '240px' },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [sentinel, hasMore, loading, onLoadMore])
}
